import { useState, useEffect, useCallback } from 'react'
import { supabase } from '@/lib/supabase'
import type { Database } from '@/lib/database.types'

type ProgressSheetInsert = Database['public']['Tables']['relationship_progress_sheets']['Insert']

export interface ChecklistItem { key: string; label: string; checkedAt: string | null }

const CHECKLIST_DEFS: { key: string; label: string }[] = [
  { key: 'item_a_vehicle_components_at',    label: 'A. Demonstrates proper and safe use of all basic vehicle components' },
  { key: 'item_b_vehicle_control_at',       label: 'B. Explains the importance of vehicle control and its effect on safe driving' },
  { key: 'item_c_technique_reasons_at',     label: 'C. Explains reasons for using/avoiding specific driving techniques' },
  { key: 'item_d_emotions_decisions_at',    label: 'D. Demonstrates appropriate control over emotions/decision-making/positive attitude' },
  { key: 'item_e_predict_anticipate_at',    label: 'E. Demonstrates ability to predict and anticipate the behaviours of all road-users' },
  { key: 'item_f_communication_at',         label: 'F. Demonstrates appropriate communication with other road-users' },
  { key: 'item_g_distraction_management_at', label: 'G. Demonstrates effective management of driver distractions/attention/risk perception' },
  { key: 'item_h_observation_skills_at',    label: 'H. Demonstrates safe, responsible and proper observation skills' },
]

export function useProgressSheet(relationshipId: string | undefined) {
  const [items, setItems] = useState<ChecklistItem[]>([])
  const [remarks, setRemarks] = useState('')
  const [studentSignedAt, setStudentSignedAt] = useState<string | null>(null)
  const [instructorSignedAt, setInstructorSignedAt] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)

  const fetchSheet = useCallback(async () => {
    if (!relationshipId) return
    setLoading(true)
    const { data } = await supabase
      .from('relationship_progress_sheets')
      .select('*')
      .eq('relationship_id', relationshipId)
      .maybeSingle()
    const row = data as Record<string, unknown> | null
    setItems(CHECKLIST_DEFS.map(d => ({ ...d, checkedAt: (row?.[d.key] as string | null) ?? null })))
    setRemarks(data?.instructor_remarks ?? '')
    setStudentSignedAt(data?.student_signed_at ?? null)
    setInstructorSignedAt(data?.instructor_signed_at ?? null)
    setLoading(false)
  }, [relationshipId])

  useEffect(() => { fetchSheet() }, [fetchSheet])

  const toggleItem = useCallback(async (key: string, checked: boolean) => {
    if (!relationshipId) return
    setSaving(true)
    try {
      await supabase.from('relationship_progress_sheets').upsert(
        { relationship_id: relationshipId, [key]: checked ? new Date().toISOString() : null } as ProgressSheetInsert,
        { onConflict: 'relationship_id' }
      )
      await fetchSheet()
    } finally {
      setSaving(false)
    }
  }, [relationshipId, fetchSheet])

  const saveRemarks = useCallback(async (text: string) => {
    if (!relationshipId) return
    setSaving(true)
    try {
      await supabase.from('relationship_progress_sheets').upsert(
        { relationship_id: relationshipId, instructor_remarks: text },
        { onConflict: 'relationship_id' }
      )
    } finally {
      setSaving(false)
    }
  }, [relationshipId])

  const sign = useCallback(async (who: 'student' | 'instructor') => {
    if (!relationshipId) return
    setSaving(true)
    try {
      const column = who === 'student' ? 'student_signed_at' : 'instructor_signed_at'
      await supabase.from('relationship_progress_sheets').upsert(
        { relationship_id: relationshipId, [column]: new Date().toISOString() } as ProgressSheetInsert,
        { onConflict: 'relationship_id' }
      )
      await fetchSheet()
    } finally {
      setSaving(false)
    }
  }, [relationshipId, fetchSheet])

  return { items, remarks, setRemarks, studentSignedAt, instructorSignedAt, loading, saving, toggleItem, saveRemarks, sign }
}

export interface PaymentRecord { id: string; paymentDate: string; amount: number; balance: number | null }

export function usePaymentRecords(relationshipId: string | undefined) {
  const [records, setRecords] = useState<PaymentRecord[]>([])
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)

  const fetchRecords = useCallback(async () => {
    if (!relationshipId) return
    setLoading(true)
    const { data } = await supabase
      .from('relationship_payment_records')
      .select('id, payment_date, amount, balance')
      .eq('relationship_id', relationshipId)
      .order('payment_date', { ascending: false })
    setRecords((data ?? []).map((r: any) => ({ id: r.id, paymentDate: r.payment_date, amount: Number(r.amount), balance: r.balance != null ? Number(r.balance) : null })))
    setLoading(false)
  }, [relationshipId])

  useEffect(() => { fetchRecords() }, [fetchRecords])

  const addRecord = useCallback(async (paymentDate: string, amount: number, balance: number | null) => {
    if (!relationshipId) return { ok: false }
    setSaving(true)
    try {
      const { error } = await supabase.from('relationship_payment_records').insert({
        relationship_id: relationshipId, payment_date: paymentDate, amount, balance,
      })
      if (error) return { ok: false, error: error.message }
      await fetchRecords()
      return { ok: true }
    } finally {
      setSaving(false)
    }
  }, [relationshipId, fetchRecords])

  return { records, loading, saving, addRecord }
}
